import { ColliExtendedClient } from '@frontend/colli/api';
import { ColliStateBadge } from '@frontend/colli/common';
import { ColliExtended } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { FormattedMessage } from 'react-intl';

import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import useCollis from './collis.controller';

const ID = 'pudo-collis';

export const Collis = () => {
    const viewProps = useCollis();

    return (
        <div
            id={ID}
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader title={CommonMessage.OBJECTS.PARCEL.PLURAL} />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['all', 'active', 'completed', 'cancelled', 'failed']}
                    displayMap={
                        new Map([
                            [
                                'all',
                                <FormattedMessage
                                    id='feature.transactions.tabs.all'
                                    defaultMessage='All'
                                    key='feature.collis.tabs.all'
                                />
                            ],
                            [
                                'active',
                                <FormattedMessage
                                    id='feature.transactions.tabs.active'
                                    defaultMessage='Active'
                                    key='feature.collis.tabs.active'
                                />
                            ],
                            [
                                'completed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.completed'
                                    defaultMessage='Completed'
                                    key='feature.collis.tabs.completed'
                                />
                            ],
                            [
                                'cancelled',
                                <FormattedMessage
                                    id='feature.transactions.tabs.cancelled'
                                    description='Tab label for cancelled PuDo transactions'
                                    defaultMessage='Cancelled'
                                    key='feature.collis.tabs.cancelled'
                                />
                            ],
                            [
                                'failed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.failed'
                                    defaultMessage='Failed'
                                    key='feature.collis.tabs.failed'
                                />
                            ]
                        ])
                    }
                    defaultActive='active'
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <CollisList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const CollisList = (viewProps: ReturnType<typeof useCollis>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    return (
        <ReactiveObjectList<ColliExtended>
            id={viewProps.listId}
            useStaticListId
            fetch={(arg) =>
                ColliExtendedClient.fetchExtendedCollis({
                    account_id: viewProps.currentAccount,
                    ...(viewProps.stateFilter && { state: viewProps.stateFilter }),
                    ...arg
                })
            }
            cardClassName='card bg-background-surface w-full border border-border-default'
            dispatch={viewProps.dispatch}
            type={EntityType.COLLI}
            eventListener={EntityType.COLLI}
            activeFilter={{
                searchKeys: ['label'],
                filterKeys: ['transaction_id', 'spot_id', 'current_spot_id', 'slot_id', 'current_slot_id', 'after', 'before'],
                displayName: new Map([
                    ['label', CommonMessage.OBJECTS.PARCEL.DEFAULT],
                    ['transaction_id', CommonMessage.OBJECTS.TRANSACTION.DEFAULT],
                    ['spot_id', CommonMessage.OBJECTS.SPOT.DESTINATION],
                    [
                        'current_spot_id',
                        <FormattedMessage
                            key='pudo.collis.filter.current-spot'
                            id='pudo.collis.filter.current-spot'
                            defaultMessage='Current SPOT'
                        />
                    ],
                    ['slot_id', CommonMessage.OBJECTS.SLOT.DESTINATION],
                    [
                        'current_slot_id',
                        <FormattedMessage
                            key='pudo.collis.filter.current-slot'
                            id='pudo.collis.filter.current-slot'
                            description='Filter label for the current SLOT of a colli'
                            defaultMessage='Current SLOT'
                        />
                    ],
                    ['after', CommonMessage.FORMS.FROM_TIMESTAMP],
                    ['before', CommonMessage.FORMS.TO_TIMESTAMP]
                ]),
                ...(viewProps.report && {
                    export: {
                        modalId: viewProps.reportModalId,
                        report: viewProps.report,
                        data: viewProps.exportData,
                        dataKeys: ['current_spot_id']
                    }
                })
            }}
            sortingColumns={['label', 'creation_timestamp']}
            columns={{
                columns: ['label', 'type', 'state', 'transaction_id', 'current_spot_id', 'current_slot_id', 'creation_timestamp'],
                headerOverwrite: new Map([
                    ['label', CommonMessage.OBJECTS.PARCEL.DEFAULT],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE],
                    ['transaction_id', CommonMessage.OBJECTS.TRANSACTION.DEFAULT],
                    [
                        'current_spot_id',
                        <FormattedMessage
                            key='pudo.collis.header.current-spot'
                            id='pudo.collis.header.current-spot'
                            defaultMessage='Current SPOT'
                        />
                    ],
                    [
                        'current_slot_id',
                        <FormattedMessage
                            key='pudo.collis.header.current-slot'
                            id='pudo.collis.header.current-slot'
                            defaultMessage='Current SLOT'
                        />
                    ],
                    ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                ]),
                customCells: new Map([
                    [
                        'type',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <DefaultBadge
                                    styles={{ container: { color: 'bg-brand-muted' } }}
                                    label={
                                        obj.row.original.type
                                            ? (obj.row.original.type.charAt(0).toUpperCase() + obj.row.original.type.slice(1)).replace(/_/g, ' ')
                                            : '/'
                                    }
                                />
                            </td>
                        )
                    ],
                    [
                        'state',
                        (obj) => (
                            <td className={defaultCellClassName}>
                                <ColliStateBadge state={obj.row.original.state} />
                            </td>
                        )
                    ],
                    [
                        'transaction_id',
                        (obj) => (
                            <td
                                key={obj.row.original.transaction?.entity_id}
                                className={defaultCellClassName}>
                                {obj.row.original.transaction ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.transaction.entity_id}
                                        entityType={EntityType.TRANSACTION}
                                        display={obj.row.original.transaction.name}
                                        key={obj.row.original.transaction.entity_id}
                                        foreign={obj.row.original.transaction.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.transaction)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'current_spot_id',
                        (obj) => (
                            <td
                                key={obj.row.original.current_spot?.entity_id}
                                className={defaultCellClassName}>
                                {obj.row.original.current_spot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.current_spot.entity_id}
                                        entityType={EntityType.SPOT}
                                        display={obj.row.original.current_spot.name}
                                        key={obj.row.original.current_spot.entity_id}
                                        foreign={obj.row.original.current_spot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.current_spot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'current_slot_id',
                        (obj) => (
                            <td
                                key={obj.row.original.current_slot?.entity_id}
                                className={defaultCellClassName}>
                                {obj.row.original.current_slot ? (
                                    <PlatformObjectBadge
                                        id={obj.row.original.current_slot.entity_id}
                                        entityType={EntityType.SLOT}
                                        display={obj.row.original.current_slot.name}
                                        key={obj.row.original.current_slot.entity_id}
                                        parentIds={{
                                            spot_id: obj.row.original.current_slot.data.spot_id,
                                            module_id: obj.row.original.current_slot.data.module_id
                                        }}
                                        foreign={obj.row.original.current_slot.foreign}
                                        accountId={accountIdFromResolvedEntity(obj.row.original.current_slot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ]
                ])
            }}
            onSelectElement={(obj) =>
                openPlatformDetail({
                    entityType: EntityType.COLLI,
                    id: obj.id,
                    parents: { transaction_id: obj.transaction_id },
                    context: { application: Application.PUDO }
                })
            }
        />
    );
};

export default Collis;
